import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Phone, Calendar, Clock, User, MapPin, CheckCircle, AlertTriangle, Flame, ThermometerSun } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { companyInfo } from '../../data/mock';

const FurnaceRepairRoseville = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFCF7]" data-testid="blog-post-furnace-repair">
      {/* Navigation Bar */}
      <div className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/blog')}
            data-testid="back-to-blog-btn"
            className="flex items-center gap-2 text-[#622B62] hover:text-[#DA9C3A] transition-colors font-medium"
          >
            <ArrowLeft size={20} />
            Back to Blog
          </button>
          <a href={`tel:${companyInfo.phone}`}>
            <Button className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-semibold px-5 py-2 rounded-full">
              <Phone size={16} className="mr-2" />
              {companyInfo.phone}
            </Button>
          </a>
        </div>
      </div>

      {/* Article Hero */}
      <div className="bg-[#622B62] text-white py-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#7A3D7A] rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#4A1F4A] rounded-full translate-y-1/2 -translate-x-1/2 opacity-50" />
        <div className="max-w-3xl mx-auto px-4 relative z-10">
          <span className="inline-block bg-[#DA9C3A] text-white text-xs font-bold px-3 py-1 rounded-full mb-4">Furnace Repair</span>
          <h1 className="romano-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight" data-testid="article-heading" data-speakable="true">
            Furnace Repair in Roseville, CA — A No-Nonsense Guide from Your Local HVAC Guy
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-white/80">
            <span className="flex items-center gap-1"><User size={14} /> Juliano Romano</span>
            <span className="flex items-center gap-1"><Calendar size={14} /> June 9, 2026</span>
            <span className="flex items-center gap-1"><Clock size={14} /> 10 min read</span>
            <span className="flex items-center gap-1"><MapPin size={14} /> Roseville, CA</span>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-4 py-12">
        <div className="prose prose-lg max-w-none">

          {/* Intro */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8 mb-10 shadow-sm" data-speakable="true">
            <p className="text-gray-700 text-lg leading-relaxed mb-4">
              It's 6 AM, it's 38 degrees outside, and your furnace just decided to take a vacation. The kids are burrowing under blankets, the dog won't come out from under the couch, and you're standing in the hallway in a bathrobe wondering why the one appliance you actually <em>need</em> chose today to quit.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed mb-0">
              I'm Juliano Romano. I've been running <strong>furnace repair calls in Roseville, CA</strong> and the surrounding area for over 20 years. If you're reading this right now because your heat just went out — take a breath. We're going to figure this out together. And if you're reading this in July because you're a planner? Even better. Let's talk furnaces.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-[#622B62]/5 rounded-2xl p-6 mb-10">
            <h2 className="romano-heading text-xl font-bold text-[#622B62] mb-4">What We'll Cover</h2>
            <ul className="space-y-2">
              {[
                '"Wait, It Gets Cold in Roseville?" — Yes, and Your Furnace Knows It',
                'The Most Common Furnace Problems We See in Roseville',
                'DIY Checklist Before You Call for Furnace Repair',
                'How Much Does Furnace Repair Cost in Roseville, CA?',
                'Furnace Repair vs. Replacement — The Real Math',
                'Gas Furnace Safety — What Every Homeowner Must Know',
                'How to Keep Your Furnace Running Strong for Years',
                'Every City We Service for Furnace Repair',
                'A Word from Juliano',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <span className="text-[#DA9C3A] font-bold">{i + 1}.</span> {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Section 1 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            "Wait, It Gets Cold in Roseville?" — Yes, and Your Furnace Knows It
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            People who don't live here think Northern California is all sunshine and flip-flops. Tell that to anyone who's woken up to frost on their windshield in December. Roseville winters are mild compared to, say, Chicago — but nighttime temperatures in the 30s and 40s from November through February are enough to make your furnace work hard every single night.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            And here's the thing most people don't realize: those temperature swings are actually tougher on your furnace than consistently cold weather. Going from 55°F during the day to 34°F at night means your furnace is cycling on and off constantly, which puts wear on the igniter, the blower motor, and the heat exchanger.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            The same goes for our neighbors in Rocklin, Granite Bay, Lincoln, and especially the foothills — Auburn, Loomis, and Penryn often get 5 to 10 degrees colder than Roseville on winter nights. If you're up there, your furnace is working even harder.
          </p>

          {/* Section 2 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            The Most Common Furnace Problems We See in Roseville
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            After thousands of furnace repair calls across Roseville and the surrounding area, here are the issues we see again and again:
          </p>
          <div className="space-y-4 my-6">
            {[
              { problem: 'Igniter failure', detail: 'Modern furnaces use a hot surface igniter instead of a pilot light. These igniters are fragile — they crack from heat cycling and age. When they fail, your furnace won\'t light at all. It\'s the single most common furnace repair we do in Roseville.', freq: 'Very common' },
              { problem: 'Dirty or clogged filter', detail: 'I know, I know — everyone says this. But I\'m serious: a shocking number of our "emergency" furnace calls turn out to be a filter so clogged the system shut itself off as a safety measure. Check it before you call.', freq: 'Very common' },
              { problem: 'Thermostat issues', detail: 'Dead batteries, incorrect settings, or a thermostat that\'s lost its programming. Before assuming the worst, make sure your thermostat is set to HEAT, the temperature is set above the current room temp, and the batteries aren\'t dead.', freq: 'Common' },
              { problem: 'Blower motor failure', detail: 'The blower is what pushes warm air through your ducts. When it fails, your furnace might fire up and get hot, but no warm air reaches your rooms. You might hear humming but feel no airflow from the vents.', freq: 'Common' },
              { problem: 'Flame sensor corrosion', detail: 'The flame sensor is a safety device that confirms your burners are lit. Over time it corrodes and can\'t detect the flame, so the furnace shuts down as a precaution. A quick cleaning often fixes this — it\'s a 15-minute repair.', freq: 'Common' },
              { problem: 'Cracked heat exchanger', detail: 'This is the serious one. The heat exchanger is what separates combustion gases from the air in your home. A crack can allow carbon monoxide into your living space. If we find a cracked heat exchanger, we\'ll shut the furnace down immediately. Your family\'s safety comes first.', freq: 'Less common' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-2 flex-1">
                    <Flame size={18} className="text-[#DA9C3A] mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-bold text-[#622B62]">{item.problem}</span>
                      <p className="text-gray-600 mt-1 mb-0 text-sm">{item.detail}</p>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-[#DA9C3A] bg-[#DA9C3A]/10 px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0">{item.freq}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Section 3 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            DIY Checklist Before You Call for Furnace Repair
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Look — I'd rather you fix it yourself and not need me than pay for a service call over something simple. Try these before picking up the phone:
          </p>
          <div className="grid gap-3 my-6">
            {[
              { step: 'Check the thermostat', why: 'Set to HEAT. Temperature set higher than current room temp. Fresh batteries if it\'s battery-powered. Sounds obvious, but it solves the problem more often than you\'d think.' },
              { step: 'Check the air filter', why: 'Pull it out. If it\'s grey and solid, replace it. A clogged filter can trigger a safety lockout. Pop in a new one and give it 10 minutes.' },
              { step: 'Check the circuit breaker', why: 'Furnaces have their own breaker. If it\'s tripped, reset it. If it trips again right away, stop — call us.' },
              { step: 'Check the gas supply', why: 'Make sure the gas valve near your furnace is in the ON position (handle parallel to the pipe). Also check that your gas meter is on.' },
              { step: 'Check the furnace switch', why: 'There\'s usually a light switch on or near the furnace. Sounds silly, but people accidentally turn it off. Make sure it\'s on.' },
              { step: 'Look for a flashing light', why: 'Most modern furnaces have a small LED light on the control board. The pattern of flashes is a diagnostic code. Write it down — it\'ll help us diagnose the issue faster when you call.' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                <div className="w-8 h-8 rounded-full bg-[#622B62] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm font-bold">{i + 1}</span>
                </div>
                <div>
                  <span className="font-bold text-[#622B62]">{item.step}</span>
                  <p className="text-gray-600 mt-1 mb-0 text-sm">{item.why}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Section 4 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            How Much Does Furnace Repair Cost in Roseville, CA?
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Straight talk on pricing — because you deserve to know what's fair before anyone shows up at your door:
          </p>
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden my-6">
            <div className="bg-[#622B62] text-white p-4">
              <h3 className="font-bold text-lg mb-0">Typical Furnace Repair Costs — Roseville, CA (2026)</h3>
            </div>
            <div className="divide-y">
              {[
                { repair: 'Igniter replacement', range: '$150 – $350', note: 'Most common repair we do' },
                { repair: 'Flame sensor cleaning/replacement', range: '$100 – $250', note: 'Quick fix, big impact' },
                { repair: 'Blower motor replacement', range: '$400 – $900', note: 'Depends on motor type' },
                { repair: 'Circuit board replacement', range: '$400 – $800', note: 'The furnace\'s brain' },
                { repair: 'Gas valve replacement', range: '$350 – $750', note: 'Controls gas flow to burners' },
                { repair: 'Inducer motor replacement', range: '$300 – $700', note: 'Vents combustion gases' },
                { repair: 'Heat exchanger replacement', range: '$1,500 – $3,500', note: 'Major repair — often better to replace the furnace' },
                { repair: 'Thermostat replacement', range: '$150 – $500', note: 'Depends on model (basic vs. smart)' },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div className="flex-1">
                    <span className="font-medium text-gray-800">{item.repair}</span>
                    <p className="text-gray-500 text-xs mb-0">{item.note}</p>
                  </div>
                  <span className="font-bold text-[#622B62] text-sm">{item.range}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Section 5 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            Furnace Repair vs. Replacement — The Real Math
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Here's my rule of thumb, and I'll share it with you the same way I'd tell my own family:
          </p>
          <div className="bg-[#DA9C3A]/10 border-l-4 border-[#DA9C3A] p-5 rounded-r-xl my-6">
            <p className="text-gray-800 font-medium mb-0">
              <strong>The 50% Rule:</strong> If the repair costs more than 50% of a new furnace, and your furnace is over 15 years old, replacement usually makes more financial sense. A new furnace costs $3,500–$7,000 installed in the Roseville area (depending on size and efficiency). So if you're looking at a $2,000 repair on a 17-year-old furnace, it's time for a conversation about replacement.
            </p>
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            But context matters. If your furnace is 8 years old and needs a $600 blower motor, that's a no-brainer repair. You've got years of life left in that unit. I'll always give you my honest opinion, and I'll never push a new system on someone who doesn't need one.
          </p>

          {/* Section 6 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            Gas Furnace Safety — What Every Roseville Homeowner Must Know
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            I'm going to get serious here for a minute, because this matters more than anything else in this article:
          </p>
          <div className="space-y-3 my-6">
            {[
              { point: 'Install carbon monoxide detectors', detail: 'On every level of your home, and especially near bedrooms. CO is colorless and odorless — you can\'t detect it without a monitor. This isn\'t optional.' },
              { point: 'Know the signs of a CO leak', detail: 'Headaches, dizziness, nausea, confusion. If multiple family members feel sick at the same time, get out of the house and call 911. Then call us.' },
              { point: 'Never ignore a yellow or flickering burner flame', detail: 'A healthy furnace flame is blue. If it\'s yellow, orange, or flickering, that\'s incomplete combustion — and potentially dangerous. Call us immediately.' },
              { point: 'Don\'t block furnace vents or intake pipes', detail: 'Your furnace needs fresh air to combust gas safely. Make sure exhaust vents and fresh air intakes aren\'t blocked by snow, leaves, or stored items.' },
              { point: 'Get annual inspections', detail: 'A yearly furnace inspection catches cracked heat exchangers, gas leaks, and electrical issues before they become dangerous. It\'s the single best thing you can do for your family\'s safety.' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-red-50 rounded-xl p-4 border border-red-100">
                <AlertTriangle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-bold text-red-800">{item.point}</span>
                  <p className="text-gray-700 mt-1 mb-0 text-sm">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Section 7 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            How to Keep Your Furnace Running Strong for Years
          </h2>
          <div className="grid gap-4 my-6">
            {[
              { tip: 'Change your filter every 1–3 months', desc: 'Monthly during heavy use (November–February). This single habit prevents more furnace problems than anything else.' },
              { tip: 'Schedule annual maintenance in fall', desc: 'September or October is the sweet spot — before heating season hits. We\'ll catch small issues before they become cold-night emergencies.' },
              { tip: 'Keep the area around your furnace clear', desc: 'No boxes, no paint cans, no laundry. Give it 3 feet of clearance on all sides. It\'s a safety issue and an airflow issue.' },
              { tip: 'Listen to your furnace', desc: 'You know what it normally sounds like. If something changes — banging, popping, screeching — don\'t ignore it. Early catches save money.' },
              { tip: 'Don\'t close too many vents', desc: 'Same as with AC: closing vents creates pressure problems. Keep at least 80% of your vents open at all times.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <h4 className="font-bold text-[#622B62] mb-1">{item.tip}</h4>
                <p className="text-gray-600 text-sm mb-0">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Section 8 - GEO */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            Every City We Service for Furnace Repair
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Whether you're in the heart of Roseville or up in the foothills where it gets even colder, we've got you covered:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 my-6">
            {[
              { city: 'Roseville, CA', note: 'Home base — we\'re minutes away' },
              { city: 'Rocklin, CA', note: 'Fastest response after Roseville' },
              { city: 'Granite Bay, CA', note: 'Larger homes, bigger furnaces — we handle it all' },
              { city: 'Lincoln, CA', note: 'Sun City residents, we take care of you' },
              { city: 'Loomis, CA', note: 'Gets chilly up there — we know' },
              { city: 'Folsom, CA', note: 'A quick drive east' },
              { city: 'El Dorado Hills, CA', note: 'Foothills cold is no joke' },
              { city: 'Citrus Heights, CA', note: 'Long-time customers here' },
              { city: 'Orangevale, CA', note: 'Just down the road' },
              { city: 'Fair Oaks, CA', note: 'A community we love serving' },
              { city: 'Carmichael, CA', note: 'Part of our regular territory' },
              { city: 'Sacramento, CA', note: 'Greater Sacramento covered' },
              { city: 'Auburn, CA', note: 'Foothill country needs good heat' },
              { city: 'Antelope, CA', note: 'North Sacramento area' },
              { city: 'Penryn, CA', note: 'Rural Placer County — we\'re there' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <MapPin size={16} className="text-[#DA9C3A] mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-gray-800 font-medium text-sm">{item.city}</span>
                  <p className="text-gray-500 text-xs mb-0">{item.note}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Section 9 - Personal */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4">
            A Word from Juliano
          </h2>
          <div className="bg-[#622B62]/5 rounded-2xl p-8 my-6 border border-[#622B62]/10">
            <p className="text-gray-700 leading-relaxed mb-4">
              Furnace problems feel more urgent than AC problems. There's something about cold that makes people anxious in a way that heat doesn't. I get it. When it's 35 degrees and your heat is out, comfort isn't a luxury — it's a necessity, especially if you have kids, elderly parents, or pets at home.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              That's why we take furnace calls seriously. When someone in Roseville calls us because their heat is out, we don't say "we'll get to you in three days." We hustle. Because I know what it feels like to sit in a cold house watching your breath, waiting for someone to show up.
            </p>
            <p className="text-gray-700 leading-relaxed mb-0">
              If your furnace is acting up — or if it hasn't been serviced in a while and you want peace of mind before winter hits — give us a call. We'll take care of you like family. That's what we do.
            </p>
            <div className="mt-6 pt-6 border-t border-[#622B62]/10">
              <p className="font-bold text-[#622B62] mb-0">— Juliano Romano</p>
              <p className="text-gray-500 text-sm mb-0">Owner, Romano Heating and Cooling · LIC #1077499</p>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#622B62] to-[#4A1F4A] rounded-3xl p-10 text-center text-white relative overflow-hidden my-12">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10">
              <h3 className="romano-heading text-2xl sm:text-3xl font-bold mb-4">
                Need Furnace Repair in Roseville? We're On It.
              </h3>
              <p className="text-white/90 mb-8 max-w-xl mx-auto">
                Whether it's a 2 AM emergency or you just want to get ahead of winter, give us a call. Free estimates, honest pricing, no pressure.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:9162749021">
                  <Button className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-bold px-10 py-6 text-lg rounded-full shadow-xl">
                    <Phone size={22} className="mr-2" />
                    Call (916) 274-9021
                  </Button>
                </a>
                <Button
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#622B62] font-bold px-10 py-6 text-lg rounded-full"
                  onClick={() => navigate('/#contact')}
                >
                  Get a Free Estimate
                </Button>
              </div>
            </div>
          </div>

          {/* Related Links */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 my-8">
            <h4 className="font-bold text-[#622B62] mb-3">More from Romano Heating & Cooling</h4>
            <ul className="space-y-2">
              <li><Link to="/maintenance-plans" className="text-[#DA9C3A] font-medium hover:underline">Maintenance Plans — Protect your furnace starting at $9.99/mo</Link></li>
              <li><Link to="/blog/ac-repair-roseville-ca" className="text-[#DA9C3A] font-medium hover:underline">AC Repair in Roseville CA — Complete Guide</Link></li>
              <li><Link to="/services/heating-maintenance" className="text-[#DA9C3A] font-medium hover:underline">Heating Maintenance Services</Link></li>
              <li><Link to="/faq" className="text-[#DA9C3A] font-medium hover:underline">Frequently Asked Questions</Link></li>
            </ul>
          </div>

          {/* Author Box */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-start gap-5 mt-8">
            <img src={companyInfo.logo} alt="Romano Heating & Cooling" className="w-16 h-16 rounded-lg object-contain flex-shrink-0" />
            <div>
              <h4 className="font-bold text-[#622B62]">Juliano Romano</h4>
              <p className="text-sm text-gray-500 mb-2">Owner, Romano Heating & Cooling</p>
              <p className="text-gray-600 text-sm">
                Juliano has been providing honest, dependable furnace repair in Roseville, CA since 2005. He and his team keep homes warm across Roseville, Rocklin, Folsom, and the greater Sacramento region every winter. California contractor license #1077499.
              </p>
            </div>
          </div>

        </div>
      </article>

      {/* Footer */}
      <div className="bg-[#622B62] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={companyInfo.logo} alt="Romano Heating & Cooling" className="h-24 w-24 object-contain rounded-lg" />
            <div className="text-left">
              <h3 className="font-bold romano-heading">Romano Heating & Cooling</h3>
              <p className="text-white/70 text-sm">1316 Church Street, Roseville, CA 95678</p>
            </div>
          </div>
          <p className="text-[#DA9C3A] font-semibold mb-2">(916) 274-9021</p>
          <p className="text-white/60 text-sm">&copy; {new Date().getFullYear()} Romano Heating and Cooling. All Rights Reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default FurnaceRepairRoseville;
